import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StytchProvider, useStytch, useStytchSession, useStytchUser } from '@stytch/react';
import { stytch, clearStytchLocalSession } from '@/modules/auth/stytchClient';
import { eventBus } from '@/modules/core/events';
import { events } from '../events';
import { authApi } from '../api';
import * as Sentry from '@sentry/browser';
import { trackSignIn, trackSignOut, trackUserIdentification } from '@/services/analytics';
import { setLocalPasskeyEnrolled } from '@/modules/auth/utils/passkeyLocal';
import { setAuthFetchSignOutHandler } from '@/modules/auth/utils/authFetch';
import { LIVE_MATCH_KEYS } from '@/modules/game/utils/liveMatchStorage';
import { LoadingState } from '@/modules/ui/components';
import { Navigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

function mapUser(stytchUser) {
  if (!stytchUser) return null;
  const email =
    stytchUser.emails?.find((e) => e.verified)?.email ||
    stytchUser.emails?.[0]?.email ||
    null;
  return {
    id: stytchUser.user_id,
    email,
    webauthn_registrations: stytchUser.webauthn_registrations || [],
  };
}

function readSessionJwt(stytchClient, stytchSession) {
  if (!stytchSession) return null;
  try {
    return stytchClient.session.getTokens?.()?.session_jwt || null;
  } catch {
    return null;
  }
}

function AuthProviderInner({ children }) {
  const stytchClient = useStytch();
  const { session: stytchSession } = useStytchSession();
  const { user: stytchUser } = useStytchUser();
  const [hasRecordedLogin, setHasRecordedLogin] = useState(false);
  const previousUserIdRef = useRef(null);
  const currentUserEmailRef = useRef(null);
  // Track whether Stytch hooks have settled at least once (avoid flash of Auth Required).
  const [hydrated, setHydrated] = useState(false);

  const clearUserSpecificData = (reason = 'unknown') => {
    try {
      const keysToRemove = [
        'pending_match_data',
        'subscription_just_upgraded',
        'match_saved',
        'opponent_name',
        'current_squad_id',
        'return_url_after_upgrade',
        ...LIVE_MATCH_KEYS,
      ];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      console.log(`Cleared user-specific localStorage due to: ${reason}`);
    } catch (error) {
      Sentry.captureException(error, { extra: { reason, context: 'clearUserSpecificData' } });
    }
  };

  const user = useMemo(() => mapUser(stytchUser), [stytchUser]);

  // Synchronous JWT — avoids user-before-token race that sent Bearer undefined.
  const sessionJwt = useMemo(
    () => readSessionJwt(stytchClient, stytchSession),
    [stytchClient, stytchSession, stytchUser]
  );

  useEffect(() => {
    setHydrated(true);
  }, [stytchSession, stytchUser]);

  // loading until hydrated; if a session exists, wait until both user + JWT are ready
  const loading =
    !hydrated ||
    (Boolean(stytchSession) && (!user || !sessionJwt));

  useEffect(() => {
    const userId = user?.id || null;
    const email = user?.email || null;
    const previousId = previousUserIdRef.current;
    const previousEmail = currentUserEmailRef.current;

    if (previousEmail !== email) {
      if (previousEmail && email) {
        clearUserSpecificData(`user_change_from_${previousEmail}_to_${email}`);
        // A different user means a fresh login to record
        setHasRecordedLogin(false);
      } else if (previousEmail && !email && !userId) {
        clearUserSpecificData(`user_signout_${previousEmail}`);
      }
      currentUserEmailRef.current = email;
    }

    if (!previousId && userId) {
      console.log('New user', email, 'signed in');
      eventBus.publish(events.USER_SIGNED_IN, { user });
      setHasRecordedLogin(false);
      trackSignIn('email');
      trackUserIdentification(userId, { email });
      if ((stytchUser?.webauthn_registrations || []).length > 0) {
        setLocalPasskeyEnrolled(true, email);
      }
    } else if (previousId && !userId) {
      eventBus.publish(events.USER_SIGNED_OUT, {});
      setHasRecordedLogin(false);
      trackSignOut();
    }

    previousUserIdRef.current = userId;
  }, [user, stytchUser]);

  const session = useMemo(() => {
    if (!sessionJwt || !user) return null;
    return {
      access_token: sessionJwt,
      user,
    };
  }, [sessionJwt, user]);

  useEffect(() => {
    if (session?.user?.email && !hasRecordedLogin) {
      authApi.recordUserLogin(session.user.email);
      setHasRecordedLogin(true);
    }
  }, [session?.user?.email, hasRecordedLogin]);

  // Never leaves a stale session behind: the server-side revoke is
  // best-effort (it can fail on a network blip or an already-odd session),
  // but the local Stytch cookies/localStorage are always cleared afterwards
  // so a signed-out user can never get silently stuck on the old account.
  const signOut = async () => {
    try {
      await stytchClient.session.revoke();
    } catch (error) {
      console.error('Error revoking session server-side (still clearing locally):', error);
      Sentry.captureException(error);
    } finally {
      clearStytchLocalSession();
    }
  };

  useEffect(() => {
    setAuthFetchSignOutHandler(async () => {
      try {
        await stytchClient.session.revoke();
      } finally {
        clearStytchLocalSession();
      }
    });
    return () => setAuthFetchSignOutHandler(null);
  }, [stytchClient]);

  const contextValue = {
    session,
    user,
    loading,
    signOut,
    sessionJwt,
    stytch: stytchClient,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }) {
  return (
    <StytchProvider stytch={stytch}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </StytchProvider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuthContext();
  const location = useLocation();

  if (loading) {
    return <LoadingState fullPage message="Loading..." />;
  }

  if (!user) {
    // Logged-out visitors (e.g. coaches clicking an email link) go to login
    // and are returned here afterwards — never a dead end.
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return children;
}

const POST_LOGIN_KEY = 'post_login_redirect';

function isSafeRedirect(to) {
  return typeof to === 'string' && to.startsWith('/') && !to.startsWith('//');
}

/**
 * Remember where to send the coach after sign-in. Stored in sessionStorage
 * so it survives the OAuth round-trip through the identity provider.
 */
export function rememberPostLoginRedirect(from) {
  try {
    if (isSafeRedirect(from)) sessionStorage.setItem(POST_LOGIN_KEY, from);
  } catch {
    // Storage unavailable — sign-in still works, just lands on /squads.
  }
}

/**
 * Read-and-clear the stored post-login destination (defaults to /squads).
 */
export function consumePostLoginRedirect() {
  try {
    const to = sessionStorage.getItem(POST_LOGIN_KEY);
    sessionStorage.removeItem(POST_LOGIN_KEY);
    if (isSafeRedirect(to)) return to;
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return '/squads';
}

export function PublicRoute({ children }) {
  return children;
}
